import React, { useState, useRef } from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIButton from '@/components/shared/UIButton';
import './EditPropertyUnitsImport.scss';

interface EditPropertyUnitsImportProps {
  property?: any;
  loading?: boolean;
  collapsed?: boolean;
  onToggleCollapse: () => void;
  onUnitsImported?: (units: any[]) => void;
}

const EditPropertyUnitsImport: React.FC<EditPropertyUnitsImportProps> = ({
                                                                           property,
                                                                           loading,
                                                                           collapsed,
                                                                           onToggleCollapse,
                                                                           onUnitsImported,
                                                                         }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Перевіряємо тип файлу
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please select XLS or CSV file');
      return;
    }

    // Перевіряємо розмір (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB в байтах
    if (file.size > maxSize) {
      alert('File size should not exceed 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // TODO: Implement actual file upload logic
      console.log('Uploading file:', selectedFile.name);

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock imported units data
      const mockUnits = [
        { unitNumber: '101', type: 'apartment', area: 65 },
        { unitNumber: '102', type: 'apartment', area: 70 },
        { unitNumber: '103', type: 'apartment', area: 55 },
      ];

      onUnitsImported?.(mockUnits);
      setSelectedFile(null);

      console.log('File uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <UICollapsibleSection
      title="Units batch import"
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading units import..."
      className="EditPropertyUnitsImport"
    >
      <div className="EditPropertyUnitsImport__upload">
        <div
          className={`EditPropertyUnitsImport__upload-area ${
            dragOver ? 'EditPropertyUnitsImport__upload-area--drag-over' : ''
          } ${
            selectedFile ? 'EditPropertyUnitsImport__upload-area--has-file' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {!selectedFile ? (
            <>
              <div className="EditPropertyUnitsImport__upload-text">
                Drag and drop document here or
              </div>
              <UIButton
                variant="primary"
                onClick={handleChooseFile}
                disabled={uploading}
              >
                Browse files
              </UIButton>
              <div className="EditPropertyUnitsImport__upload-hint">
                XLS, CSV / max 10MB
              </div>
            </>
          ) : (
            <div className="EditPropertyUnitsImport__file-selected">
              <div className="EditPropertyUnitsImport__file-info">
                <div className="EditPropertyUnitsImport__file-name">
                  {selectedFile.name}
                </div>
                <div className="EditPropertyUnitsImport__file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
              <div className="EditPropertyUnitsImport__file-actions">
                <UIButton
                  variant="primary"
                  onClick={handleUpload}
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </UIButton>
                <UIButton
                  variant="secondary"
                  outline
                  onClick={handleCancel}
                  disabled={uploading}
                >
                  Cancel
                </UIButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
    </UICollapsibleSection>
  );
};

export default EditPropertyUnitsImport;